import { useMemo } from 'react';

import { formatMoney } from 'src/app/utils/formatters';

import { getRequirementsFromRequirementStepState } from '../../../shared/helpers';
import { useDiscountWizardStateContext } from '../../../state';
import { ThresholdType } from '../../../types';

import type { RequirementsStepState } from '../../../state/requirements';

type HiddenGamificationCardConfiguration = {
  type: 'hidden';
};

type BaseGamificationCardConfiguration = {
  descriptionText: string;
  title: string;
};

type NumberOfItemsGamificationCardConfiguration = BaseGamificationCardConfiguration & {
  numberOfItems: number;
  numberOfProductSlots: number;
  type: 'numberOfItems';
};

type SpendingAmountGamificationCardConfiguration = BaseGamificationCardConfiguration & {
  spendingAmount: number;
  type: 'spendingAmount';
};

export type GamificationCardConfiguration =
  | HiddenGamificationCardConfiguration
  | NumberOfItemsGamificationCardConfiguration
  | SpendingAmountGamificationCardConfiguration;

const HIDDEN_GAMIFICATION_CARD_CONFIGURATION: HiddenGamificationCardConfiguration = { type: 'hidden' };

export function useGamificationCardConfiguration(): GamificationCardConfiguration {
  const {
    onlineDetailsStepState: { onlineName },
    requirementsStepState,
  } = useDiscountWizardStateContext();

  const gamificationCardConfiguration = useMemo(
    () => ({ ...getGamificationCardConfiguration(requirementsStepState, onlineName) }),
    [requirementsStepState, onlineName]
  );

  return gamificationCardConfiguration;
}

export function getGamificationCardConfiguration(
  requirementsStepState: RequirementsStepState,
  title: string
): GamificationCardConfiguration {
  const requirements = getRequirementsFromRequirementStepState(requirementsStepState);
  // only show gamification card if there is exactly one requirement
  if (Object.keys(requirements).length !== 1) {
    return HIDDEN_GAMIFICATION_CARD_CONFIGURATION;
  }

  const requirement = Object.values(requirements)[0];

  switch (requirement.condition) {
    case ThresholdType.NoThreshold:
      return HIDDEN_GAMIFICATION_CARD_CONFIGURATION;
    case ThresholdType.NumberOfItems:
      return {
        type: 'numberOfItems',
        numberOfItems: Number(requirement.minimumItems),
        numberOfProductSlots: Number(requirement.minimumItems) > 10 ? 0 : Number(requirement.minimumItems),
        title,
        descriptionText: `Add ${requirement.minimumItems || 0} eligible item${
          Number(requirement.minimumItems || 0) === 1 ? '' : 's'
        } to complete the offer.`,
      };
    case ThresholdType.SpendingAmount:
      return {
        type: 'spendingAmount',
        spendingAmount: Number(requirement.minimumSpend),
        title,
        descriptionText: `Add ${formatMoneyWithTrimmedCents(requirement.minimumSpend)} to complete the offer.`,
      };
    case ThresholdType.ProductWeight:
      return HIDDEN_GAMIFICATION_CARD_CONFIGURATION;
    default:
      return HIDDEN_GAMIFICATION_CARD_CONFIGURATION;
  }
}

// Most discounts are an even dollar amount and we want to trim the cents for those cases
const formatMoneyWithTrimmedCents = (value: number | string): string => formatMoney(value).replace(/\.00$/, '');
